import { BasicRadioGroup } from '@fl/cmsch-fe-library';
import React, { useCallback, memo } from 'react';
import { opt, Opt } from 'ts-opt';
import { Ant } from 'common/ant';
import styles from './styles.sass';

/** Presentation model for a single language choice in the shell language picker. */
export interface LanguageOption {
  /** Language code used by i18n and persistence. */
  language: string;
  /** Label shown in the language picker. */
  label: string;
  /** Icon rendered alongside the language label. */
  icon: React.ReactElement;
}

/** Props for the shared language picker used in the full and side app menus. */
interface Props {
  /** Chooses popover placement and trigger behavior for the side menu. */
  isSideMenu: boolean;
  /** Radio options rendered inside the language picker popover. */
  languageOptions: Array<LanguageOption>;
  /** The currently active language choice. */
  selectedLanguage: LanguageOption;
  /** Called when the user selects a different language. */
  onLanguageChange(language: string): void;
}
const LanguageSelectBase: React.FC<Props> = props => {
  const {
    isSideMenu,
    languageOptions,
    selectedLanguage,
    onLanguageChange
  } = props;
  const handleLanguageChange = useCallback((language: Opt<string>) => onLanguageChange(language.orCrash('must have value')), [onLanguageChange]);
  return <span data-test-id="language-select" data-sentry-component="LanguageSelectBase" data-sentry-source-file="language-select.tsx">
            <Ant.Popover trigger={isSideMenu ? 'click' : 'hover'} placement={isSideMenu ? 'rightBottom' : 'bottomRight'} align={{
      offset: isSideMenu ? [-16, -11] : [-16, -16]
    }} /* eslint-disable-line no-magic-numbers */ content={<div className={styles.languageSelectRadioGroup}>
                        <BasicRadioGroup value={opt(selectedLanguage.language)} onFieldChange={handleLanguageChange} options={languageOptions.map(({
        language,
        label,
        icon
      }) => ({
        value: language,
        label,
        icon
      }))} />
                    </div>} data-sentry-element="unknown" data-sentry-source-file="language-select.tsx">
                <div className={styles.languageSelect}>
                    {selectedLanguage.icon}
                </div>
            </Ant.Popover>
        </span>;
};

/**
 * Renders the shared shell language picker for full and side menus.
 *
 * @param props Language picker configuration and current selection.
 * @returns The shell language picker popover.
 *
 * @example
 * <LanguageSelect
 *     isSideMenu={false}
 *     languageOptions={languageOptions}
 *     selectedLanguage={selectedLanguage}
 *     onLanguageChange={handleLanguageChange}
 * />
 */
export const LanguageSelect = memo(LanguageSelectBase);